/**
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║  LIFE & WEALTH SOLUTIONS — UNIFIED API SERVICE                       ║
 * ║  Version: 31.0.0 | Production | 0 Placeholders | 0 TODOs            ║
 * ║  Replaces: mockDataService.ts + mockAnalyticsService.ts              ║
 * ║  Single source of truth for all backend communication                ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// ─────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true' || localStorage.getItem('demo_mode') === 'true';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface User {
  user_id: string;
  email: string;
  full_name: string;
  role: 'agent12' | 'admin' | 'viewer';
  agency_id: string;
  is_active: boolean;
  last_login?: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
}

export interface WhaleOpportunity {
  policy_id: string;
  client_name: string;
  client_location: string;
  total_premium: number;
  commission_value: number;
  policy_type: string;
  agent_9_notes: string;
  agent_11_status: 'approved' | 'pending' | 'rejected';
  compliance_checks: {
    state_vetted: boolean;
    carrier_approved: boolean;
    bias_check_passed: boolean;
  };
  created_at: string;
}

export interface PendingApproval {
  approval_id: string;
  policy_id: string;
  client_name: string;
  policy_type: string;
  premium_amount: number;
  risk_level: 'low' | 'medium' | 'high';
  tier: 1 | 2 | 3;
  agent_recommendation: string;
  compliance_status: string;
  created_at: string;
  expires_at: string;
}

export interface SystemMetrics {
  health_score: number;
  uptime_percentage: number;
  active_agents: number;
  total_agents: number;
  policies_processed_today: number;
  compliance_rate: number;
  self_healing_events: number;
  api_response_time_ms: number;
  last_updated: string;
}

export interface AnalyticsDashboard {
  revenue: { date: string; amount: number }[];
  policies_bound: { date: string; count: number }[];
  leads: { date: string; count: number }[];
  performance: { metric: string; value: number; trend: number }[];
  whale_count: number;
  compliance_rate: number;
  agent_efficiency: number;
}

export interface Lead {
  lead_id: string;
  full_name: string;
  email: string;
  phone: string;
  status: string;
  score: number;
  policy_interest: string;
  estimated_premium: number;
  created_at: string;
  assigned_agent?: string;
}

export interface Notification {
  notification_id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  action_url?: string;
}

export interface NotificationCounts {
  total: number;
  unread: number;
  urgent: number;
}

export interface PricingCalculation {
  base_fee: number;
  policy_bind_fees: number;
  compliance_fees: number;
  whale_share: number;
  total_monthly: number;
  legacy_comparison: {
    vertafore_monthly: number;
    applied_systems_monthly: number;
    savings_monthly: number;
    savings_annual: number;
    roi_multiplier: number;
  };
  tier: string;
}

export interface OnboardingStatus {
  user_id: string;
  completion_percentage: number;
  current_step: number;
  steps: {
    step1_identity: boolean;
    step2_whale_threshold: boolean;
    step3_autonomic_permission: boolean;
    step4_billing: boolean;
    step5_hitl_oath: boolean;
  };
  completed_at?: string;
}

export interface ActivitySummary {
  agent_id: string;
  actions_today: number;
  policies_reviewed: number;
  approvals_granted: number;
  alerts_generated: number;
  compliance_checks: number;
  last_action_at: string;
  performance_score: number;
}

// ─────────────────────────────────────────────
// AXIOS INSTANCE — SINGLETON
// ─────────────────────────────────────────────
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Version': '31.0.0',
    },
  });

  // REQUEST INTERCEPTOR — attach JWT
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // RESPONSE INTERCEPTOR — handle 401 token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          if (!refreshToken) {
            localStorage.clear();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          localStorage.setItem('access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }

          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return client(originalRequest);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

const apiClient = createApiClient();

// ─────────────────────────────────────────────
// DEMO MODE TOGGLE
// ─────────────────────────────────────────────
export const isDemoMode = (): boolean => {
  return DEMO_MODE || localStorage.getItem('demo_mode') === 'true';
};

export const setDemoMode = (enabled: boolean): void => {
  localStorage.setItem('demo_mode', enabled ? 'true' : 'false');
};

// ─────────────────────────────────────────────
// AUTH API
// ─────────────────────────────────────────────
export const authApi = {
  login: async (email: string, password: string): Promise<{ user: User; tokens: AuthTokens }> => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token || '');
    }
    return data;
  },

  register: async (payload: {
    email: string;
    password: string;
    full_name: string;
    role: string;
    agency_id: string;
  }): Promise<{ user: User; tokens: AuthTokens }> => {
    const { data } = await apiClient.post('/auth/register', payload);
    return data;
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },

  me: async (): Promise<User> => {
    const { data } = await apiClient.get('/auth/me');
    return data;
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/users/forgot-password', { email });
  },

  refresh: async (): Promise<AuthTokens> => {
    const refreshToken = localStorage.getItem('refresh_token');
    const { data } = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
    localStorage.setItem('access_token', data.access_token);
    return data;
  },
};

// ─────────────────────────────────────────────
// AGENT 12 / HITL DASHBOARD API
// ─────────────────────────────────────────────
export const agent12Api = {
  getDashboard: async (userId: string): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get(`/agent12/dashboard/${userId}`);
    return data;
  },

  getWhaleOpportunities: async (): Promise<WhaleOpportunity[]> => {
    const { data } = await apiClient.get('/agent12/whale-opportunities');
    return data.items || data;
  },

  getPendingApprovals: async (): Promise<PendingApproval[]> => {
    const { data } = await apiClient.get('/agent12/pending-approvals');
    return data.items || data;
  },

  approvePolicy: async (policyId: string, notes?: string): Promise<void> => {
    await apiClient.post(`/compliance/approve/${policyId}`, { notes });
  },

  rejectPolicy: async (policyId: string, reason: string): Promise<void> => {
    await apiClient.post(`/compliance/reject/${policyId}`, { reason });
  },

  getActivitySummary: async (agentId: string): Promise<ActivitySummary> => {
    const { data } = await apiClient.get(`/agent12/activity-summary/${agentId}`);
    return data;
  },
};

// ─────────────────────────────────────────────
// SYSTEM INTEGRITY API
// ─────────────────────────────────────────────
export const systemApi = {
  getHealthScore: async (): Promise<{ score: number; status: string; details: Record<string, number> }> => {
    const { data } = await apiClient.get('/system-integrity/health-score');
    return data;
  },

  getMetrics: async (): Promise<SystemMetrics> => {
    const { data } = await apiClient.get('/system-integrity/metrics');
    return data;
  },
};

// ─────────────────────────────────────────────
// ANALYTICS API
// ─────────────────────────────────────────────
export const analyticsApi = {
  getDashboard: async (days: number = 30): Promise<AnalyticsDashboard> => {
    const { data } = await apiClient.get(`/analytics/dashboard?days=${days}`);
    return data;
  },

  getRevenue: async (days: number = 30): Promise<{ date: string; amount: number }[]> => {
    const { data } = await apiClient.get(`/analytics/revenue?days=${days}`);
    return data.items || data;
  },

  getLeadAnalytics: async (days: number = 30): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get(`/analytics/leads?days=${days}`);
    return data;
  },

  getPerformance: async (days: number = 30): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get(`/analytics/performance?days=${days}`);
    return data;
  },

  getABTestingDashboard: async (): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get('/ab-testing/dashboard');
    return data;
  },

  getExperiment: async (experimentId: string): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get(`/ab-testing/experiments/${experimentId}`);
    return data;
  },
};

// ─────────────────────────────────────────────
// LEAD SCORING API
// ─────────────────────────────────────────────
export const leadScoringApi = {
  getAnalytics: async (): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get('/lead-scoring/analytics');
    return data;
  },

  getTopLeads: async (limit: number = 100): Promise<Lead[]> => {
    const { data } = await apiClient.get(`/lead-scoring/top-leads?limit=${limit}`);
    return data.items || data;
  },

  getModels: async (): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get('/lead-scoring/models');
    return data.items || data;
  },

  getFeatureImportance: async (): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get('/lead-scoring/feature-importance');
    return data;
  },

  getLeadScoreHistory: async (leadId: string): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get(`/lead-scoring/lead/${leadId}/score-history`);
    return data.items || data;
  },

  scoreLead: async (leadId: string): Promise<{ score: number; tier: string }> => {
    const { data } = await apiClient.post(`/lead-scoring/score/${leadId}`);
    return data;
  },

  batchScore: async (leadIds: string[]): Promise<Record<string, number>> => {
    const { data } = await apiClient.post('/lead-scoring/batch-score', { lead_ids: leadIds });
    return data;
  },

  trainModel: async (config: Record<string, unknown>): Promise<{ job_id: string }> => {
    const { data } = await apiClient.post('/lead-scoring/train-model', config);
    return data;
  },

  activateModel: async (modelId: string): Promise<void> => {
    await apiClient.post(`/lead-scoring/models/${modelId}/activate`);
  },
};

// ─────────────────────────────────────────────
// ML MODEL API
// ─────────────────────────────────────────────
export const mlApi = {
  getDashboard: async (): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get('/ml/dashboard');
    return data;
  },

  getModels: async (): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get('/ml/models');
    return data.items || data;
  },

  getDatasets: async (): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get('/ml/datasets');
    return data.items || data;
  },

  getTrainingJobs: async (limit: number = 10): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get(`/ml/training-jobs?limit=${limit}`);
    return data.items || data;
  },

  createTrainingJob: async (payload: Record<string, unknown>): Promise<{ job_id: string }> => {
    const { data } = await apiClient.post('/ml/training-jobs', payload);
    return data;
  },

  deployModel: async (modelId: string): Promise<void> => {
    await apiClient.post(`/ml/models/${modelId}/deploy`);
  },
};

// ─────────────────────────────────────────────
// NOTIFICATIONS API
// ─────────────────────────────────────────────
export const notificationsApi = {
  getAll: async (limit: number = 50): Promise<Notification[]> => {
    const { data } = await apiClient.get(`/notifications?limit=${limit}`);
    return data.items || data;
  },

  getCounts: async (): Promise<NotificationCounts> => {
    const { data } = await apiClient.get('/notifications/counts');
    return data;
  },

  getPreferences: async (): Promise<Record<string, boolean>> => {
    const { data } = await apiClient.get('/notifications/preferences');
    return data;
  },

  getStats: async (): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get('/notifications/stats');
    return data;
  },

  markRead: async (notificationId: string): Promise<void> => {
    await apiClient.put(`/notifications/${notificationId}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await apiClient.put('/notifications/read-all');
  },

  delete: async (notificationId: string): Promise<void> => {
    await apiClient.delete(`/notifications/${notificationId}`);
  },

  updatePreferences: async (preferences: Record<string, boolean>): Promise<void> => {
    await apiClient.put('/notifications/preferences', preferences);
  },
};

// ─────────────────────────────────────────────
// LEADS API
// ─────────────────────────────────────────────
export const leadsApi = {
  getAll: async (params?: { status?: string; limit?: number; page?: number }): Promise<PaginatedResponse<Lead>> => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const { data } = await apiClient.get(`/leads${query ? '?' + query : ''}`);
    return data;
  },

  getActive: async (limit: number = 50): Promise<Lead[]> => {
    const { data } = await apiClient.get(`/leads?status=active&limit=${limit}`);
    return data.items || data;
  },

  getById: async (leadId: string): Promise<Lead> => {
    const { data } = await apiClient.get(`/leads/${leadId}`);
    return data;
  },

  create: async (lead: Partial<Lead>): Promise<Lead> => {
    const { data } = await apiClient.post('/leads', lead);
    return data;
  },

  update: async (leadId: string, updates: Partial<Lead>): Promise<Lead> => {
    const { data } = await apiClient.put(`/leads/${leadId}`, updates);
    return data;
  },

  delete: async (leadId: string): Promise<void> => {
    await apiClient.delete(`/leads/${leadId}`);
  },
};

// ─────────────────────────────────────────────
// AUTOMATION API
// ─────────────────────────────────────────────
export const automationApi = {
  getTriggers: async (): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get('/automation/triggers');
    return data.items || data;
  },

  getEventTypes: async (): Promise<string[]> => {
    const { data } = await apiClient.get('/automation/events/types');
    return data;
  },

  getActionTypes: async (): Promise<string[]> => {
    const { data } = await apiClient.get('/automation/actions/types');
    return data;
  },

  createTrigger: async (trigger: Record<string, unknown>): Promise<{ trigger_id: string }> => {
    const { data } = await apiClient.post('/automation/triggers', trigger);
    return data;
  },

  deleteTrigger: async (triggerId: string): Promise<void> => {
    await apiClient.delete(`/automation/triggers/${triggerId}`);
  },
};

// ─────────────────────────────────────────────
// PREDICTIVE / CHURN API
// ─────────────────────────────────────────────
export const predictiveApi = {
  getTopOpportunities: async (limit: number = 20): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get(`/predictive/opportunities/top?limit=${limit}`);
    return data.items || data;
  },

  getHighRiskChurn: async (): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get('/predictive/churn/high-risk');
    return data.items || data;
  },

  getLeadConversion: async (leadId: string): Promise<{ probability: number; factors: string[] }> => {
    const { data } = await apiClient.get(`/predictive/leads/${leadId}`);
    return data;
  },

  batchConversionPrediction: async (leadIds: string[]): Promise<Record<string, number>> => {
    const { data } = await apiClient.post('/predictive/leads/batch/conversion', { lead_ids: leadIds });
    return data;
  },
};

// ─────────────────────────────────────────────
// PRICING API (Outcome-Based Model)
// ─────────────────────────────────────────────
export const pricingApi = {
  calculate: async (params: {
    policies_bound: number;
    whale_count: number;
    whale_total_premium: number;
    compliance_certs: number;
    billing_tier?: string;
  }): Promise<PricingCalculation> => {
    const { data } = await apiClient.post('/pricing/calculate', params);
    return data;
  },

  getTiers: async (): Promise<Record<string, unknown>[]> => {
    const { data } = await apiClient.get('/pricing/tiers');
    return data;
  },

  compareLegacy: async (params: Record<string, number>): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.post('/pricing/compare', params);
    return data;
  },
};

// ─────────────────────────────────────────────
// ONBOARDING API (Agent 12 Flow)
// ─────────────────────────────────────────────
export const onboardingApi = {
  getStatus: async (userId: string): Promise<OnboardingStatus> => {
    const { data } = await apiClient.get(`/onboarding/status/${userId}`);
    return data;
  },

  step1Identity: async (payload: {
    npn: string;
    carrier_type: string;
    carrier_name: string;
    state_licenses: string[];
  }): Promise<{ message: string; next_step: number }> => {
    const { data } = await apiClient.post('/onboarding/step1/identity', payload);
    return data;
  },

  step2WhaleThreshold: async (payload: {
    whale_threshold: number;
    risk_appetite: string;
  }): Promise<{ message: string; next_step: number }> => {
    const { data } = await apiClient.post('/onboarding/step2/whale-threshold', payload);
    return data;
  },

  step3AutonomicPermission: async (payload: {
    autonomic_enabled: boolean;
    notification_preference: 'weekly_report' | 'realtime_sms' | 'silent_log';
  }): Promise<{ message: string; next_step: number }> => {
    const { data } = await apiClient.post('/onboarding/step3/autonomic-permission', payload);
    return data;
  },

  step4Billing: async (payload: {
    billing_tier: string;
    payment_method_added: boolean;
  }): Promise<{ message: string; next_step: number }> => {
    const { data } = await apiClient.post('/onboarding/step4/billing-calibration', payload);
    return data;
  },

  step5HITLOath: async (oathAccepted: boolean): Promise<{
    message: string;
    workforce_active: boolean;
    next_steps: string[];
  }> => {
    const { data } = await apiClient.post('/onboarding/step5/hitl-oath', {
      oath_accepted: oathAccepted,
    });
    return data;
  },
};

// ─────────────────────────────────────────────
// POLICIES API
// ─────────────────────────────────────────────
export const policiesApi = {
  getById: async (policyId: string): Promise<Record<string, unknown>> => {
    const { data } = await apiClient.get(`/policies/${policyId}`);
    return data;
  },
};

// ─────────────────────────────────────────────
// USERS API
// ─────────────────────────────────────────────
export const usersApi = {
  getMe: async (): Promise<User> => {
    const { data } = await apiClient.get('/users/me');
    return data;
  },

  updateMe: async (updates: Partial<User>): Promise<User> => {
    const { data } = await apiClient.put('/users/me', updates);
    return data;
  },
};

// ─────────────────────────────────────────────
// GENERIC REQUEST HELPER
// (for any endpoint not covered above)
// ─────────────────────────────────────────────
export const apiRequest = async <T = unknown>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  endpoint: string,
  payload?: unknown,
  config?: AxiosRequestConfig
): Promise<T> => {
  let response: AxiosResponse<T>;

  switch (method) {
    case 'get':
      response = await apiClient.get<T>(endpoint, config);
      break;
    case 'post':
      response = await apiClient.post<T>(endpoint, payload, config);
      break;
    case 'put':
      response = await apiClient.put<T>(endpoint, payload, config);
      break;
    case 'patch':
      response = await apiClient.patch<T>(endpoint, payload, config);
      break;
    case 'delete':
      response = await apiClient.delete<T>(endpoint, config);
      break;
  }

  return response.data;
};

// ─────────────────────────────────────────────
// EXPORT DEFAULT CLIENT (for direct use if needed)
// ─────────────────────────────────────────────
export default apiClient;

// ─────────────────────────────────────────────
// ADMIN USER MANAGEMENT API (V33)
// ─────────────────────────────────────────────
export const adminUserApi = {
  listUsers: async (params?: { role?: string; is_active?: boolean; skip?: number; limit?: number }) => {
    const { data } = await apiClient.get('/users', { params });
    return data;
  },

  createUser: async (payload: {
    full_name: string; email: string; role: string;
    password: string; agency_id: string; phone?: string; license_number?: string;
  }) => {
    const { data } = await apiClient.post('/users', payload);
    return data;
  },

  updateUser: async (userId: string, payload: {
    full_name?: string; role?: string; phone?: string; license_number?: string; is_active?: boolean;
  }) => {
    const { data } = await apiClient.put(`/users/${userId}`, payload);
    return data;
  },

  resetPassword: async (userId: string, newPassword: string) => {
    const { data } = await apiClient.post(`/users/${userId}/reset-password`, { new_password: newPassword });
    return data;
  },

  deactivateUser: async (userId: string) => {
    const { data } = await apiClient.post(`/users/${userId}/deactivate`);
    return data;
  },

  activateUser: async (userId: string) => {
    const { data } = await apiClient.post(`/users/${userId}/activate`);
    return data;
  },

  deleteUser: async (userId: string) => {
    await apiClient.delete(`/users/${userId}`);
  },

  getMyProfile: async () => {
    const { data } = await apiClient.get('/users/me/profile');
    return data;
  },

  updateMyProfile: async (payload: { full_name?: string; phone?: string; license_number?: string }) => {
    const { data } = await apiClient.put('/users/me/profile', payload);
    return data;
  },

  changeMyPassword: async (currentPassword: string, newPassword: string) => {
    const { data } = await apiClient.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return data;
  },
};
